import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash, ClockCounterClockwise, DownloadSimple, UserPlus, FileArrowUp } from "@phosphor-icons/react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { AdminTabs } from "../components/admin/AdminTabs";
import { TrustedBrowsers } from "../components/TrustedBrowsers";
import { CleanupTestPatients } from "../components/admin/CleanupTestPatients";
import { SeenToggle } from "../components/admin/SeenToggle";

const CATEGORIES = [
  { value: "letter", label: "Clinic letter" },
  { value: "result", label: "Test result" },
  { value: "imaging", label: "Imaging report" },
];

const CATEGORY_LABEL = {
  letter: "Clinic letter",
  result: "Test result",
  imaging: "Imaging report",
  patient_upload: "Patient upload",
};

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminPatients() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [error, setError] = useState("");

  // Invite form
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);

  // Upload form
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("letter");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Access log modal
  const [logFor, setLogFor] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);

  // Filter
  const [search, setSearch] = useState("");

  const loadPatients = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/patients");
      setPatients(data);
      setSelected((s) => s || data[0] || null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, []);

  const loadDocs = useCallback(async (patientId) => {
    if (!patientId) return setDocs([]);
    setLoadingDocs(true);
    try {
      const { data } = await api.get(`/admin/documents?patient_id=${patientId}`);
      setDocs(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => { loadPatients(); }, [loadPatients]);
  useEffect(() => { loadDocs(selected?.id); }, [loadDocs, selected?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) =>
      (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q)
    );
  }, [patients, search]);

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteName) return;
    setInviting(true);
    try {
      const { data } = await api.post("/admin/patients/invite", {
        email: inviteEmail.trim(), name: inviteName.trim(),
      });
      toast.success(`Invite sent to ${data.email}`);
      setInviteEmail(""); setInviteName(""); setInviteOpen(false);
      await loadPatients();
      setSelected({ id: data.user_id, email: data.email, name: inviteName.trim(), role: "patient" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setInviting(false);
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Select a patient first.");
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file.");
    if (!title.trim()) return toast.error("Add a title.");
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25 MB.");
    const ok = ["application/pdf", "image/jpeg", "image/png"];
    if (!ok.includes(file.type)) return toast.error("PDF, JPG or PNG only.");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    fd.append("category", category);
    fd.append("patient_id", selected.id);
    setUploading(true);
    try {
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Document released to ${selected.name}. Notification email sent.`);
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      await loadDocs(selected.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await fetch(`${API}/admin/documents/${doc.id}/download`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? The patient will no longer be able to see it.`)) return;
    try {
      await api.delete(`/admin/documents/${doc.id}`);
      toast.success("Deleted.");
      await loadDocs(selected.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openLog = async (doc) => {
    setLogFor(doc);
    setLoadingLog(true);
    setLogEntries([]);
    try {
      const { data } = await api.get(`/admin/documents/${doc.id}/access-log`);
      setLogEntries(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingLog(false);
    }
  };

  return (
    <div data-testid="admin-patients-page" className="container-ccc py-12">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Admin · {user?.email}</div>
          <h1 className="font-serif text-4xl mt-2">Patients & documents</h1>
          <AdminTabs active="/admin/patients" />
          <p className="mt-3 text-ink-muted text-sm max-w-2xl">
            Invite patients to their secure portal and view documents released earlier. Document uploads are currently switched off.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <button
            type="button"
            onClick={() => setInviteOpen((v) => !v)}
            data-testid="admin-invite-toggle"
            className="btn-secondary inline-flex items-center gap-1.5"
          >
            <UserPlus size={16} /> Invite a patient
          </button>
          <CleanupTestPatients onDone={() => { setSelected(null); loadPatients(); }} />
          <TrustedBrowsers />
        </div>
      </div>

      {error && <div className="mt-6 text-sm text-destructive" data-testid="admin-error">{error}</div>}

      {inviteOpen && (
        <form onSubmit={invite} data-testid="admin-invite-form" className="mt-6 p-5 bg-cream-100 border border-bronze-light/40 rounded-md grid sm:grid-cols-12 gap-3 items-end">
          <label className="sm:col-span-4 block">
            <span className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">Full name</span>
            <input required type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
              data-testid="admin-invite-name"
              className="w-full rounded-sm border border-stone-300 bg-white px-3 py-2 text-sm" />
          </label>
          <label className="sm:col-span-5 block">
            <span className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">Email</span>
            <input required type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              data-testid="admin-invite-email"
              className="w-full rounded-sm border border-stone-300 bg-white px-3 py-2 text-sm" />
          </label>
          <button disabled={inviting} type="submit" data-testid="admin-invite-submit" className="sm:col-span-3 btn-primary">
            {inviting ? "Sending…" : "Send portal invite"}
          </button>
        </form>
      )}

      <div className="mt-8 grid lg:grid-cols-12 gap-8">
        {/* Patient list */}
        <aside className="lg:col-span-4">
          <input
            type="search"
            placeholder="Search patients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-patient-search"
            className="w-full rounded-sm border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bronze-dark/40 focus:border-bronze-dark"
          />
          <ul className="mt-4 divide-y divide-stone-200/60 border border-stone-200/60 rounded-md bg-white max-h-[60vh] overflow-auto" data-testid="admin-patient-list">
            {filtered.length === 0 && (
              <li className="p-4 text-sm text-ink-muted">No patients yet — invite one to get started.</li>
            )}
            {filtered.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setSelected(p)}
                  data-testid={`admin-patient-${p.id}`}
                  className={`w-full text-left p-4 hover:bg-cream-100 ${selected?.id === p.id ? "bg-cream-100" : ""}`}
                >
                  <div className="font-serif text-base">{p.name}</div>
                  <div className="text-xs text-ink-muted">{p.email}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Documents for selected patient */}
        <section className="lg:col-span-8">
          {!selected ? (
            <div className="p-8 bg-cream-100 border border-bronze-light/40 rounded-md text-sm text-ink-muted">
              Select a patient to view and release documents.
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="eyebrow">Patient</div>
                  <h2 className="font-serif text-3xl mt-1">{selected.name}</h2>
                  <div className="text-sm text-ink-muted">{selected.email}</div>
                  <div className="mt-2"><SeenToggle email={selected.email} testId="patient-seen-toggle" /></div>
                </div>
              </div>

              <p data-testid="admin-upload-disabled" className="mt-6 p-5 bg-white border border-stone-200/60 rounded-md text-sm text-ink-muted">
                Document uploads are switched off. Existing documents below can still be viewed.
              </p>

              <div className="mt-6">
                {loadingDocs ? (
                  <div className="text-sm text-ink-muted">Loading documents…</div>
                ) : docs.length === 0 ? (
                  <div data-testid="admin-docs-empty" className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md text-sm text-ink-muted">
                    No documents released to this patient yet.
                  </div>
                ) : (
                  <ul className="divide-y divide-stone-200/60 border border-stone-200/60 rounded-md bg-white" data-testid="admin-docs-list">
                    {docs.map((d) => (
                      <li key={d.id} data-testid={`admin-doc-${d.id}`} className="p-4 flex items-start gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-serif text-lg truncate">{d.title}</span>
                            <span className="text-[10px] uppercase tracking-widest rounded-full border bg-cream-100 text-bronze-dark border-bronze-light/60 px-2 py-0.5">
                              {CATEGORY_LABEL[d.category] || d.category}
                            </span>
                          </div>
                          <div className="text-xs text-ink-muted mt-1">
                            {new Date(d.uploaded_at).toLocaleString()} · {fmtSize(d.file_size)} · {d.file_name} · uploaded by {d.uploaded_by_email}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => download(d)} data-testid={`admin-doc-download-${d.id}`}
                            className="text-sm text-bronze-dark hover:text-bronze inline-flex items-center gap-1">
                            <DownloadSimple size={14} /> View
                          </button>
                          <button type="button" onClick={() => openLog(d)} data-testid={`admin-doc-log-${d.id}`}
                            className="text-sm text-ink hover:text-bronze-dark inline-flex items-center gap-1">
                            <ClockCounterClockwise size={14} /> Audit
                          </button>
                          <button type="button" onClick={() => remove(d)} data-testid={`admin-doc-delete-${d.id}`}
                            className="text-sm text-destructive hover:opacity-80 inline-flex items-center gap-1">
                            <Trash size={14} /> Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {logFor && (
        <div data-testid="admin-log-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md max-w-lg w-full p-6 border border-stone-200/60">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="eyebrow">Access log</div>
                <h3 className="font-serif text-xl mt-1">{logFor.title}</h3>
              </div>
              <button type="button" onClick={() => setLogFor(null)} className="text-sm text-ink-muted hover:text-ink">Close</button>
            </div>
            <div className="mt-5 max-h-80 overflow-auto" data-testid="admin-log-entries">
              {loadingLog ? (
                <div className="text-sm text-ink-muted">Loading…</div>
              ) : logEntries.length === 0 ? (
                <div className="text-sm text-ink-muted">No access events recorded.</div>
              ) : (
                <ul className="divide-y divide-stone-200/60">
                  {logEntries.map((l) => (
                    <li key={l.id} className="py-2 text-sm">
                      <span className="font-medium capitalize">{l.action}</span>
                      <span className="text-ink-muted"> · {new Date(l.at).toLocaleString()} · {l.user_email} ({l.user_role})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
